/**
 * deepspace deploy
 *
 * Builds the app with Vite (Cloudflare plugin bundles both client + worker),
 * then uploads to the deploy worker which handles Cloudflare WfP deployment.
 *
 * Uses the same build pipeline as dev for full fidelity. Reads the output
 * wrangler.json (via .wrangler/deploy/config.json) to find the built assets
 * and worker bundle — the same contract that `wrangler deploy` uses.
 */

import { defineCommand } from 'citty'
import { execSync } from 'node:child_process'
import { randomUUID } from 'node:crypto'
import { readFileSync, readdirSync, existsSync } from 'node:fs'
import { join, resolve, dirname } from 'node:path'
import { pathToFileURL } from 'node:url'
import * as p from '@clack/prompts'
import { createSpinner } from '../lib/spinner'
import { ensureInstallReady } from '../lib/install-status'
import { ensureToken } from '../auth'
import { ENVS, extractCustomDevVars, parseDevVars } from '../env'
import {
  bindingManifestFromOutputConfig,
  validateBindingManifest,
  RESERVED_BINDING_NAMES,
} from '../../server/rooms/binding-manifest'
import { resolveAppName } from '../../server/rooms/app-name'
import {
  resolveAppNameForEnv, devVarsPathFor, readWranglerConfig, hasWranglerConfig,
  type WranglerConfig,
} from '../lib/wrangler-env'

const DEPLOY_URL = process.env.DEEPSPACE_DEPLOY_URL ?? ENVS.prod.deploy
const API_URL = process.env.DEEPSPACE_API_URL ?? ENVS.prod.api

const CLOUDFLARE_DEPLOY_ERROR_HINT =
  'Deployment reached the DeepSpace deploy worker, but Cloudflare\'s deployment ' +
  'control-plane API failed. This is often caused by a Cloudflare Dashboard/API ' +
  'incident or Workers for Platforms entitlement outage. Already deployed apps ' +
  'should keep serving; wait for Cloudflare to recover and retry.'

/**
 * SDK-reserved `run_worker_first` prefixes. Apps can't override or
 * shadow these; only declare additional patterns. The CLI strips the
 * reserved ones before forwarding so the platform never sees them
 * twice — keeps the deploy-side merge a clean set-union.
 */
const RESERVED_RUN_WORKER_FIRST = new Set([
  '/api/*',
  '/ws/*',
  '/internal/*',
  '/v1/*',
  '/_deepspace/*',
])

function extractRunWorkerFirst(cfg: { assets?: { run_worker_first?: unknown } }): string[] {
  const raw = cfg.assets?.run_worker_first
  if (!Array.isArray(raw)) return []
  const out: string[] = []
  const seen = new Set<string>()
  for (const entry of raw) {
    if (typeof entry !== 'string') continue
    const trimmed = entry.trim()
    if (!trimmed.startsWith('/')) continue
    if (RESERVED_RUN_WORKER_FIRST.has(trimmed)) continue
    if (seen.has(trimmed)) continue
    seen.add(trimmed)
    out.push(trimmed)
  }
  return out
}

function isLikelyCloudflareDeployFailure(status: number, error: string | undefined): boolean {
  if (status >= 500) return true
  if (!error) return false
  return [
    'Upload session failed',
    'Asset upload failed',
    'Worker deploy failed',
    'entitlements.not_available',
    'Internal Server Error',
  ].some((needle) => error.includes(needle))
}

function formatDeployWorkerError(status: number, error: string | undefined): string {
  const detail = error ?? `Deployment error (${status})`
  if (!isLikelyCloudflareDeployFailure(status, error)) return detail
  return `${CLOUDFLARE_DEPLOY_ERROR_HINT}\n\nUnderlying error: ${detail}`
}

/**
 * Must stay <= the deploy worker's MAX_UPLOAD_GROUP_BYTES, otherwise the worker
 * rejects a group with a 413 that postWithRetry won't retry (it's a 4xx),
 * dead-ending the deploy. The DEEPSPACE_DEPLOY_GROUP_BYTES override is clamped
 * to this.
 */
const MAX_GROUP_BYTES = 40 * 1024 * 1024

/**
 * Soft cap on the serialized size of one asset *group*. We pack whole asset
 * entries into groups under this size and POST each group on its own, so no
 * single request carries the entire payload (a ~40MB+ body stalls and gets
 * reset mid-upload on a marginal uplink: `write EPIPE`). A single asset larger
 * than this is sent alone in its own group — we never split an individual
 * asset. Overridable via DEEPSPACE_DEPLOY_GROUP_BYTES, clamped to MAX_GROUP_BYTES.
 */
const GROUP_BYTES = (() => {
  const raw = Number(process.env.DEEPSPACE_DEPLOY_GROUP_BYTES)
  const v = Number.isInteger(raw) && raw > 0 ? raw : 3 * 1024 * 1024
  return Math.min(v, MAX_GROUP_BYTES)
})()

/**
 * Pack whole asset entries into groups whose serialized JSON stays under
 * `maxBytes`. We never split an individual asset across groups, so a single
 * asset larger than `maxBytes` becomes its own (oversized) group.
 */
export function packAssetGroups<T>(assets: T[], maxBytes: number): T[][] {
  const groups: T[][] = []
  let current: T[] = []
  let currentBytes = 2 // "[]"
  for (const a of assets) {
    const entryBytes = Buffer.byteLength(JSON.stringify(a), 'utf-8') + 1 // +1 ≈ comma
    if (current.length > 0 && currentBytes + entryBytes > maxBytes) {
      groups.push(current)
      current = []
      currentBytes = 2
    }
    current.push(a)
    currentBytes += entryBytes
  }
  if (current.length > 0) groups.push(current)
  return groups
}

/**
 * POST with retry + backoff. Chunked upload exists to survive a flaky uplink,
 * so we always retry on network errors (fetch throws — e.g. `write EPIPE` when
 * the edge resets a stalled upload). Transient server statuses (408/429/5xx)
 * are only retried when `retryServerErrors` is set; client 4xx are always
 * returned as-is (caller surfaces the message). `makeInit` is called per
 * attempt so the request body is rebuilt fresh each time.
 *
 * The group-staging endpoint is an idempotent PUT, so retrying its 5xx is safe.
 * The commit endpoint runs a real (non-idempotent) deploy, so it passes
 * `retryServerErrors: false` — a 5xx means the server already received and
 * possibly acted on the request, and re-sending could double-deploy. Only a
 * thrown fetch (connection reset before/without a response) is retried there.
 */
export async function postWithRetry(
  url: string,
  makeInit: () => RequestInit,
  { attempts = 4, retryServerErrors = true }: { attempts?: number; retryServerErrors?: boolean } = {},
): Promise<Response> {
  let lastRes: Response | undefined
  let lastErr: unknown
  for (let attempt = 1; attempt <= attempts; attempt++) {
    try {
      const res = await fetch(url, makeInit())
      const isTransient =
        retryServerErrors && (res.status >= 500 || res.status === 408 || res.status === 429)
      if (!isTransient) {
        return res
      }
      lastRes = res
    } catch (err) {
      lastErr = err
    }
    if (attempt < attempts) {
      await new Promise((r) => setTimeout(r, Math.min(8_000, 500 * 2 ** (attempt - 1))))
    }
  }
  if (lastRes) return lastRes
  throw lastErr instanceof Error ? lastErr : new Error(String(lastErr))
}

/**
 * Best-effort cleanup of an upload's staged groups after a deploy gives up.
 * Fire-and-forget: a failed deploy must surface its real error, not a cleanup
 * error, so this never throws. It's not the only safety net either — the
 * deploy worker's R2 staging prefix carries a lifecycle rule that reaps
 * anything this misses (e.g. the CLI process is killed before it can abort).
 */
async function abortStagedUpload(
  appName: string,
  uploadId: string,
  totalGroups: number,
  token: string,
): Promise<void> {
  try {
    await fetch(
      `${DEPLOY_URL}/api/deploy/${appName}/assets` +
        `?uploadId=${uploadId}&totalGroups=${totalGroups}`,
      { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } },
    )
  } catch {
    // best-effort — orphaned groups are reaped by the staging lifecycle rule
  }
}

export default defineCommand({
  meta: {
    name: 'deploy',
    description: 'Build and deploy your DeepSpace app',
  },
  args: {
    dir: {
      type: 'positional',
      description: 'App directory (default: current directory)',
      required: false,
    },
    env: {
      type: 'string',
      description:
        'wrangler.toml [env.<name>] block to deploy (e.g. --env staging). ' +
        'Omit to deploy the top-level config.',
      required: false,
    },
  },
  async run({ args }) {
    const appDir = resolve(args.dir ?? '.')
    const envName = typeof args.env === 'string' && args.env.trim() ? args.env.trim() : undefined

    // ── Read app name from wrangler.toml ──────────────────────
    if (!hasWranglerConfig(appDir)) {
      console.error('No wrangler.toml found. Are you in a DeepSpace app directory?')
      process.exit(1)
    }

    ensureInstallReady(appDir)

    p.intro('Deploying DeepSpace app')

    const wranglerConfig: WranglerConfig = readWranglerConfig(appDir)
    const nameRes = resolveAppNameForEnv(wranglerConfig, envName)
    if (!nameRes.ok) {
      p.cancel(nameRes.reason)
      process.exit(1)
    }
    // Refuse noncanonical names the same way `deepspace dev` does. Without
    // this, deploy would canonicalize server bindings while client constants
    // (`SCOPE_ID = app:${APP_NAME}`) and `[vars].APP_NAME` continued to use
    // the raw name — splitting identity across surfaces. Fail-fast so the
    // dev fixes wrangler.toml once and every surface reads from the same
    // source of truth.
    const declaredName = envName
      ? wranglerConfig.env?.[envName]?.name
      : wranglerConfig.name
    if (declaredName !== nameRes.name) {
      const where = envName ? `[env.${envName}].name` : '`name`'
      p.cancel(
        `wrangler.toml: ${where} "${declaredName}" is not in canonical form. ` +
          `Update it to "${nameRes.name}" and re-run.`,
      )
      process.exit(1)
    }
    const appName = nameRes.name
    p.log.info(envName ? `App: ${appName}  (env: ${envName})` : `App: ${appName}`)

    // ── Ensure valid JWT ───────────────────────────────────────
    let token: string
    try {
      token = await ensureToken()
    } catch (err: any) {
      p.cancel(err.message)
      process.exit(1)
    }

    // ── Build with Vite (Cloudflare plugin bundles client + worker) ──
    // The Cloudflare Vite plugin reads `CLOUDFLARE_ENV` and applies the
    // matching `[env.<name>]` overrides from wrangler.toml when emitting
    // the built `dist/<app>/wrangler.json` and worker bundle. Set the env
    // var on the child process only — we don't want it leaking into other
    // shells the user might fan out from this one.
    const s = createSpinner()
    s.start('Building...')
    try {
      execSync('npx vite build', {
        cwd: appDir,
        stdio: 'pipe',
        env: envName
          ? { ...process.env, CLOUDFLARE_ENV: envName }
          : process.env,
      })
    } catch (err: any) {
      s.stop('Build failed')
      // Show both streams. Vite writes build errors to stderr, but the Rules of
      // Hooks checker (vite-plugin-checker) prints its diagnostics to stdout —
      // showing only stderr would collapse a hooks violation into a bare
      // "Command failed" and hide the exact fix the developer needs.
      const detail = [err.stdout?.toString(), err.stderr?.toString()]
        .filter(Boolean)
        .join('\n')
        .trim()
      console.error(detail || err.message)
      process.exit(1)
    }
    s.stop('Built')

    // ── Locate build output via .wrangler/deploy/config.json ──
    // This is the same contract that `wrangler deploy` uses after `vite build`.
    const deployConfigPath = join(appDir, '.wrangler', 'deploy', 'config.json')
    if (!existsSync(deployConfigPath)) {
      p.cancel('Build output config not found at .wrangler/deploy/config.json')
      process.exit(1)
    }

    const deployConfig = JSON.parse(readFileSync(deployConfigPath, 'utf-8')) as { configPath: string }
    const outputWranglerPath = resolve(dirname(deployConfigPath), deployConfig.configPath)

    if (!existsSync(outputWranglerPath)) {
      p.cancel(`Output wrangler.json not found at ${outputWranglerPath}`)
      process.exit(1)
    }

    const outputConfig = JSON.parse(readFileSync(outputWranglerPath, 'utf-8')) as {
      name?: string
      main: string
      assets?: { directory: string }
      durable_objects?: { bindings: Array<{ name: string; class_name: string }> }
      migrations?: Array<{ new_sqlite_classes?: string[] }>
    }

    // Defense in depth: the deploy CLI sends `appName` as the registry
    // slot and Cloudflare Vite plugin bakes a `name` field into the
    // built wrangler.json. They MUST agree — if the plugin ever regresses
    // on `CLOUDFLARE_ENV` application, we'd otherwise ship one
    // configuration's worker bundle under another configuration's name.
    // Fail loudly before the upload starts.
    if (outputConfig.name && outputConfig.name !== appName) {
      const envHint = envName ? ` (--env ${envName})` : ''
      p.cancel(
        `Build output mismatch: expected name "${appName}"${envHint}, ` +
          `but the built wrangler.json declares "${outputConfig.name}". ` +
          `This usually means the Cloudflare Vite plugin didn't apply ` +
          `the requested env block. Check the plugin version and re-run.`,
      )
      process.exit(1)
    }

    const workerDir = dirname(outputWranglerPath)
    const workerBundlePath = join(workerDir, outputConfig.main)
    const clientDir = outputConfig.assets?.directory
      ? resolve(workerDir, outputConfig.assets.directory)
      : null

    if (!existsSync(workerBundlePath)) {
      p.cancel(`Worker bundle not found at ${workerBundlePath}`)
      process.exit(1)
    }
    if (!clientDir || !existsSync(clientDir)) {
      p.cancel(`Client assets not found at ${clientDir}`)
      process.exit(1)
    }

    // ── Collect assets ────────────────────────────────────────
    s.start('Collecting assets...')
    const assets = collectAssets(clientDir)
    s.stop(`Collected ${assets.length} assets`)

    const workerJs = readFileSync(workerBundlePath, 'utf-8')

    // ── Extract DO manifest from build output config ───────────
    const doBindings = outputConfig.durable_objects?.bindings as Array<{ name: string; class_name: string }> | undefined
    const sqliteClasses = new Set(
      (outputConfig.migrations as Array<{ new_sqlite_classes?: string[] }> | undefined)
        ?.flatMap(m => m.new_sqlite_classes ?? []) ?? []
    )
    const doManifest = doBindings?.map(b => ({
      binding: b.name,
      className: b.class_name,
      sqlite: sqliteClasses.has(b.class_name),
    }))
    if (doManifest?.length) {
      p.log.info(`DO manifest: ${doManifest.length} binding(s)`)
    }

    // ── Extract custom (non-DO) bindings: Vectorize, AI, R2, KV, D1, etc.
    const customBindings = bindingManifestFromOutputConfig(outputConfig)
    const validation = validateBindingManifest(customBindings)
    if (!validation.valid) {
      p.cancel(
        `Invalid binding(s) in wrangler.toml:\n` +
          validation.errors.map((e) => `  • ${e.reason}`).join('\n'),
      )
      process.exit(1)
    }
    if (customBindings.length) {
      p.log.info(
        `Custom bindings: ${customBindings.map((b) => `${b.name} (${b.type})`).join(', ')}`,
      )
    }

    // ── Extract user-managed secrets from .dev.vars ───────────
    // The user-managed section of .dev.vars (everything below the SDK divider)
    // is the same set of values the dev server uses. Shipping them to prod as
    // secret_text bindings means a route that works against `deepspace dev`
    // also works after deploy without a separate `wrangler secret put` step.
    // Per-env: `.dev.vars.staging` for `--env staging`, plain `.dev.vars`
    // otherwise. Matches the Cloudflare Vite plugin's own resolution so
    // the file the SDK ships to prod is the same one the local preview
    // reads — no drift surface between dev and deploy for the same env.
    const devVarsPath = devVarsPathFor(appDir, envName)
    let userSecrets: Record<string, string> = {}
    if (existsSync(devVarsPath)) {
      const customSection = extractCustomDevVars(readFileSync(devVarsPath, 'utf-8'))
      if (customSection) {
        try {
          userSecrets = parseDevVars(customSection)
        } catch (err: any) {
          p.cancel(`.dev.vars: ${err.message}`)
          process.exit(1)
        }
      }
    }
    const userSecretNames = Object.keys(userSecrets)

    // Pre-flight: fail with a clear, file-pointing error rather than letting
    // the deploy-worker reject mid-deploy. Reserved names are platform-owned
    // (ASSETS, USAGE_EVENTS, …) and clashes with declared bindings would
    // otherwise produce duplicate-name metadata that CF rejects opaquely.
    const declaredBindingNames = new Set([
      ...customBindings.map((b) => b.name),
      ...(doManifest ?? []).map((d) => d.binding),
    ])
    for (const name of userSecretNames) {
      if (RESERVED_BINDING_NAMES.has(name)) {
        p.cancel(
          `.dev.vars: "${name}" is a reserved binding name and cannot be used as an app secret.`,
        )
        process.exit(1)
      }
      if (declaredBindingNames.has(name)) {
        p.cancel(
          `.dev.vars: "${name}" collides with a binding declared in wrangler.toml — rename one or the other.`,
        )
        process.exit(1)
      }
    }
    if (userSecretNames.length) {
      p.log.info(`App secrets (from .dev.vars): ${userSecretNames.join(', ')}`)
    }

    // ── Upload to deploy worker ───────────────────────────────
    s.start(`Deploying to ${appName}.app.space...`)

    // ── Forward custom worker-first asset routes ───────────────
    // CF Workers `[assets]` config supports a `run_worker_first` array
    // that wins over the SPA 404 fallback for matching paths. We always
    // reserve the SDK baseline (/api/*, /ws/*, /internal/*, /v1/*,
    // /_deepspace/*) on the platform side, but apps can declare extra
    // dynamic routes (e.g. /preview/*, /oauth/*, /.well-known/*) in
    // their wrangler.toml under `[assets] run_worker_first`. Pass those
    // through so the platform merges them into the deployed metadata.
    const extraRoutes = extractRunWorkerFirst(wranglerConfig)
    if (extraRoutes.length) {
      p.log.info(`Custom worker-first routes: ${extraRoutes.join(', ')}`)
    }

    // ── Choose transport: inline vs grouped ────────────────────
    // The assets array (base64-inlined file bytes) is the bulk of the upload.
    // Sending it all in one request stalls and gets reset mid-body on a
    // marginal uplink (`write EPIPE`). When it doesn't fit in a single group we
    // pack whole asset entries into groups under GROUP_BYTES, POST each group
    // on its own (retry-able) into the deploy worker's R2 staging, and
    // reference them by `uploadId` in the small commit request below. Small
    // deploys keep the original single-request inline transport.
    const assetGroups = packAssetGroups(assets, GROUP_BYTES)
    let inlineAssets: string | null = null
    let uploadId: string | null = null
    let totalGroups = 0
    if (assetGroups.length <= 1) {
      inlineAssets = JSON.stringify(assets)
    } else {
      uploadId = randomUUID()
      totalGroups = assetGroups.length
      try {
        for (let i = 0; i < totalGroups; i++) {
          const groupJson = JSON.stringify(assetGroups[i])
          s.message(`Uploading assets — group ${i + 1}/${totalGroups}...`)
          const groupRes = await postWithRetry(
            `${DEPLOY_URL}/api/deploy/${appName}/assets` +
              `?uploadId=${uploadId}&groupIndex=${i}&totalGroups=${totalGroups}`,
            () => ({
              method: 'POST',
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
              body: groupJson,
            }),
          )
          if (!groupRes.ok) {
            const errBody = (await groupRes.json().catch(() => ({}))) as { error?: string }
            // Reap the groups already staged for this upload before bailing —
            // a fresh deploy mints a new uploadId, so these would otherwise
            // never be reused.
            await abortStagedUpload(appName, uploadId, totalGroups, token)
            s.stop('Deploy failed')
            p.cancel(
              `Asset upload failed on group ${i + 1}/${totalGroups}: ` +
                `${errBody.error ?? `HTTP ${groupRes.status}`}`,
            )
            process.exit(1)
          }
        }
      } catch (err: any) {
        // postWithRetry exhausted its network-error retries and threw.
        await abortStagedUpload(appName, uploadId, totalGroups, token)
        s.stop('Deploy failed')
        p.cancel(`Asset upload failed (network): ${err?.message ?? err}`)
        process.exit(1)
      }
      s.message(`Deploying to ${appName}.app.space...`)
    }

    // Rebuilt per attempt so retried requests get a fresh body.
    const makeForm = (): FormData => {
      const form = new FormData()
      form.append('worker', new Blob([workerJs], { type: 'application/javascript' }), 'worker.js')
      if (inlineAssets !== null) {
        form.append('assets', inlineAssets)
      } else {
        form.append('uploadId', uploadId as string)
        form.append('totalGroups', String(totalGroups))
      }
      if (doManifest) {
        form.append('doManifest', JSON.stringify(doManifest))
      }
      if (customBindings.length) {
        form.append('bindingManifest', JSON.stringify(customBindings))
      }
      if (userSecretNames.length) {
        form.append('userSecrets', JSON.stringify(userSecrets))
      }
      if (extraRoutes.length) {
        form.append('extraRunWorkerFirst', JSON.stringify(extraRoutes))
      }
      return form
    }

    let res: Response
    try {
      res = await postWithRetry(
        `${DEPLOY_URL}/api/deploy/${appName}`,
        () => ({
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: makeForm(),
        }),
        // Commit runs a real deploy — don't retry its 5xx (could double-deploy);
        // only a thrown fetch (dropped connection) is retried, and the staged
        // groups survive on the worker until this request succeeds.
        { retryServerErrors: false },
      )
    } catch (err: any) {
      // The commit never got a response after its retries (dropped connection).
      // Reap the staged groups — a fresh deploy mints a new uploadId.
      if (uploadId) await abortStagedUpload(appName, uploadId, totalGroups, token)
      s.stop('Deploy failed')
      p.cancel(`Deploy request failed: ${err?.message ?? err}`)
      process.exit(1)
    }

    const body = (await res.json().catch(() => ({}))) as {
      success?: boolean
      url?: string
      error?: string
    }

    if (!res.ok || !body.success) {
      // Definitive failure — the worker won't reuse these groups (a re-run mints
      // a new uploadId), so reap them now rather than leaving them for the
      // lifecycle rule.
      if (uploadId) await abortStagedUpload(appName, uploadId, totalGroups, token)
      s.stop('Deploy failed')
      p.cancel(formatDeployWorkerError(res.status, body.error))
      process.exit(1)
    }

    // ── Wait for assets propagation ───────────────────────────
    // Cloudflare's deploy API returns "success" once the script + bindings are
    // accepted, but the assets binding can still be indexing for ~10–60s.
    // During that window any request hits CF's transitional "Assets have not
    // yet deployed" page (HTML), even on `/api/*` routes that should run the
    // worker first. Block here until we see the worker actually serving so
    // CI / smoke checks that fire immediately don't hit the flake.
    if (body.url) {
      s.message('Waiting for edge propagation...')
      const ok = await waitForAssetsReady(body.url, 90_000)
      if (!ok) {
        s.stop('Deployed (edge propagation still in progress after 90s)')
        p.log.warn('First requests may briefly hit the assets transitional page; retry in a minute.')
        p.log.success(`Live at: ${body.url}`)
        await syncSubscriptionPlans(appDir, appName, token)
        await syncOneTimeProducts(appDir, appName, token)
        p.outro('Done')
        return
      }
    }

    s.stop('Deployed!')
    p.log.success(`Live at: ${body.url}`)

    // ── Reconcile subscription plans + one-time products (optional files) ──
    // Each surfaces its own result inline but never fails the deploy — a
    // developer who hasn't finished Stripe Connect onboarding still gets a
    // working deploy; they just see a warning telling them to connect first.
    await syncSubscriptionPlans(appDir, appName, token)
    await syncOneTimeProducts(appDir, appName, token)

    p.outro('Done')
  },
})

/**
 * Bundle the app's `src/subscriptions.ts` with esbuild, dynamic-import the
 * resulting ESM, and POST the declarations to /api/subscriptions/sync.
 * No-op when the file is absent.
 */
async function syncSubscriptionPlans(appDir: string, appName: string, token: string): Promise<void> {
  const subsPath = join(appDir, 'src', 'subscriptions.ts')
  if (!existsSync(subsPath)) return

  const s = createSpinner()
  s.start('Syncing subscription plans...')

  let plans: unknown
  try {
    const esbuild = await import('esbuild')
    const outDir = join(appDir, '.wrangler', 'deploy')
    const outFile = join(outDir, 'subscriptions.bundle.mjs')
    await esbuild.build({
      entryPoints: [subsPath],
      bundle: true,
      format: 'esm',
      platform: 'node',
      target: 'node18',
      outfile: outFile,
      logLevel: 'silent',
      write: true,
    })
    // Cache-bust the import — repeat deploys in the same Node process would
    // otherwise reuse a stale module from the loader cache.
    const mod = (await import(`${pathToFileURL(outFile).href}?t=${Date.now()}`)) as {
      subscriptionPlans?: unknown
    }
    plans = mod.subscriptionPlans
  } catch (err: any) {
    s.stop('Plan sync skipped')
    p.log.warn(`Could not load src/subscriptions.ts: ${err.message}`)
    return
  }

  if (!Array.isArray(plans)) {
    s.stop('Plan sync skipped')
    p.log.warn(
      'src/subscriptions.ts must `export const subscriptionPlans = [...] as const`',
    )
    return
  }

  // The deploy has already succeeded by the time we get here — a network blip
  // or malformed response on /sync must NOT throw, or the user sees a stack
  // trace after their app is live. Anything unexpected becomes a warning.
  let res: Response
  try {
    res = await fetch(`${API_URL}/api/subscriptions/sync`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ appName, plans }),
    })
  } catch (err: any) {
    s.stop('Plan sync skipped')
    p.log.warn(`Plan sync request failed (network): ${err.message ?? err}`)
    return
  }
  const body = (await res.json().catch(() => ({}))) as {
    ok?: boolean
    error?: string
    hint?: string
    onboardingUrl?: string
    planChanges?: PlanChange[]
    connectMissing?: boolean
  }

  if (res.ok && body.ok) {
    s.stop(`Synced ${plans.length} plan${plans.length === 1 ? '' : 's'} to Stripe`)
    // Connect-not-yet-set-up is no longer a sync-blocking error. Plans land
    // in Stripe normally; the warning tells the developer their earnings
    // will queue in the platform balance until they finish Stripe Connect
    // onboarding. Once they connect, the first payout sweeps everything.
    if (body.connectMissing) {
      p.log.warn(
        'Stripe Connect not yet set up. Customers can subscribe and earnings ' +
          'will queue in your platform balance until you finish onboarding at ' +
          '/earnings on the dashboard. The first payout after you connect ' +
          'will sweep everything that accumulated in the meantime.',
      )
    }
    // Any plan change that stranded existing subscribers — whether the plan
    // was removed entirely or just had its price swapped — flows through one
    // prompt. The platform has no way to know whether the developer wants
    // those subscribers cancelled or grandfathered; this is the moment they
    // get to decide. Skipping is fine (silent grandfathering is the safe
    // default); they can always cancel later via the SDK helper.
    const changes = (body.planChanges ?? []).filter((c) => c.affectedSubscribers > 0)
    if (changes.length > 0) {
      await promptAndCancelChangedPlans(appName, token, changes)
    }
    return
  }

  s.stop('Plan sync skipped')
  p.log.warn(
    `Plan sync failed: ${body.error ?? `HTTP ${res.status}`}${
      body.hint ? ` — ${body.hint}` : ''
    }`,
  )
}

// Plan-change events the deploy CLI surfaces to the developer. Mirrors the
// /sync response shape on the platform side; kept inline here so the helper
// is self-contained.
interface PlanChange {
  slug: string
  reason: 'removed' | 'repriced'
  affectedSubscribers: number
  intervals?: Array<{ interval: 'month' | 'year'; oldCents: number; newCents: number }>
}

function formatCents(c: number): string {
  return c === 0 ? 'free' : `$${(c / 100).toFixed(2)}`
}

function summaryLine(c: PlanChange): string {
  const n = c.affectedSubscribers
  const suffix = `${n} subscriber${n === 1 ? '' : 's'}`
  if (c.reason === 'removed') {
    return `  • ${c.slug} — removed (${suffix} still on it)`
  }
  // repriced: include the interval diffs for context.
  const diffs = (c.intervals ?? [])
    .map((i) => `${i.interval}: ${formatCents(i.oldCents)} → ${formatCents(i.newCents)}`)
    .join('; ')
  return `  • ${c.slug} — repriced (${diffs}; ${suffix} grandfathered on old price)`
}

/**
 * After /sync reports plans that stranded existing subscribers (removed
 * outright OR repriced with grandfathered customers), ask the developer
 * whether to cancel those subscriptions at the end of their current billing
 * period. Skipping is fine — they can run their own cleanup later via the
 * SDK's `cancelSubscription` helper.
 *
 * One yes/no prompt covers every affected plan. Granular per-plan control
 * is intentionally not surfaced; if a developer wants to cancel for plan A
 * but keep plan B, they can re-declare A in their next deploy.
 *
 * Errors here are warnings, never deploy failures: the deploy itself
 * succeeded; cleanup is the polite-but-optional tail.
 */
async function promptAndCancelChangedPlans(
  appName: string,
  token: string,
  changes: PlanChange[],
): Promise<void> {
  const summary = changes.map(summaryLine).join('\n')
  p.log.warn(
    `Plan change${changes.length === 1 ? '' : 's'} from this deploy left subscribers stranded:\n${summary}\n\n` +
      'Stripe will keep charging them until you cancel. Cancel at the end of their\n' +
      'current billing period? (Skip to leave them grandfathered.)',
  )
  const choice = await p.confirm({
    message: `Cancel ${changes.length === 1 ? 'these subscribers' : 'all of them'} at period end?`,
    initialValue: false,
  })
  if (p.isCancel(choice) || !choice) {
    p.log.info('Skipped — existing subscribers stay on their current plan/price.')
    return
  }

  // The admin-cancel endpoint batches at 50 and returns `hasMore` when there
  // are more rows to process. Loop until done so a plan with hundreds of
  // stranded subscribers gets fully cleaned up — without this, the deploy
  // prompt's confirmation ("cancel these subscribers") would silently leave
  // the tail being charged. The cap on iterations is a safety net for the
  // unlikely "nothing makes progress" path; a fresh deploy can resume.
  const MAX_ITERATIONS = 100 // 50/batch × 100 = 5000 subs/plan max per deploy
  for (const c of changes) {
    const s = createSpinner()
    s.start(`Canceling ${c.affectedSubscribers} subscriber${c.affectedSubscribers === 1 ? '' : 's'} on ${c.slug}...`)
    let totalCanceled = 0
    let totalFailed = 0
    let iteration = 0
    let lastError: string | null = null
    let stopped = false
    try {
      while (iteration < MAX_ITERATIONS) {
        const res = await fetch(`${API_URL}/api/subscriptions/admin-cancel`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            appName,
            planSlug: c.slug,
            atPeriodEnd: true,
            reason: `plan ${c.reason} via deploy`,
          }),
        })
        const body = (await res.json().catch(() => ({}))) as {
          success?: boolean
          canceled?: number
          failures?: Array<{ stripeSubscriptionId: string; error: string }>
          hasMore?: boolean
          error?: string
        }
        if (!res.ok || !body.success) {
          // Genuine failure (auth, 5xx, etc.) — surface and stop. The empty-
          // match case returns 200 with hasMore=false, not an error, so this
          // branch only fires for real problems.
          lastError = body.error ?? `HTTP ${res.status}`
          s.stop(`Cancel failed for ${c.slug}: ${lastError}`)
          stopped = true
          break
        }
        const batchCanceled = body.canceled ?? 0
        totalCanceled += batchCanceled
        totalFailed += body.failures?.length ?? 0
        if (!body.hasMore) break
        // Progress guard: if a batch returned hasMore=true but canceled=0
        // (every row failed), we'd loop forever. Stop and report.
        if (batchCanceled === 0) {
          lastError = 'no progress (all rows failed this batch)'
          break
        }
        iteration++
      }
      if (!stopped) {
        const tail =
          totalFailed > 0
            ? `; ${totalFailed} failed (see Stripe)`
            : ''
        const cap =
          iteration >= MAX_ITERATIONS
            ? ` (batch cap reached — re-run deploy to finish remaining)`
            : ''
        const errSuffix = lastError ? ` — ${lastError}` : ''
        s.stop(`Canceled ${totalCanceled} on ${c.slug}${tail}${cap}${errSuffix}`)
      }
    } catch (err: any) {
      s.stop(`Cancel failed for ${c.slug}: ${err.message}`)
    }
  }
}

/**
 * Bundle the app's `src/products.ts` with esbuild, dynamic-import the
 * resulting ESM, and POST the declarations to /api/charges/products/sync.
 * No-op when the file is absent. Mirrors syncSubscriptionPlans — products are
 * the one-time-charge equivalent of subscription plans, and the platform
 * resolves amount/name from this catalog at checkout time so the browser
 * can't pick (productId, amount) together.
 */
async function syncOneTimeProducts(appDir: string, appName: string, token: string): Promise<void> {
  const productsPath = join(appDir, 'src', 'products.ts')
  if (!existsSync(productsPath)) return

  const s = createSpinner()
  s.start('Syncing one-time products...')

  let products: unknown
  try {
    const esbuild = await import('esbuild')
    const outDir = join(appDir, '.wrangler', 'deploy')
    const outFile = join(outDir, 'products.bundle.mjs')
    await esbuild.build({
      entryPoints: [productsPath],
      bundle: true,
      format: 'esm',
      platform: 'node',
      target: 'node18',
      outfile: outFile,
      logLevel: 'silent',
      write: true,
    })
    const mod = (await import(`${pathToFileURL(outFile).href}?t=${Date.now()}`)) as {
      oneTimeProducts?: unknown
    }
    products = mod.oneTimeProducts
  } catch (err: any) {
    s.stop('Product sync skipped')
    p.log.warn(`Could not load src/products.ts: ${err.message}`)
    return
  }

  if (!Array.isArray(products)) {
    s.stop('Product sync skipped')
    p.log.warn(
      'src/products.ts must `export const oneTimeProducts = [...] as const`',
    )
    return
  }

  // See syncSubscriptionPlans for rationale — the deploy is already live by
  // the time we get here, so network/parse failures degrade to warnings.
  let res: Response
  try {
    res = await fetch(`${API_URL}/api/charges/products/sync`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ appName, products }),
    })
  } catch (err: any) {
    s.stop('Product sync skipped')
    p.log.warn(`Product sync request failed (network): ${err.message ?? err}`)
    return
  }
  const body = (await res.json().catch(() => ({}))) as {
    ok?: boolean
    count?: number
    error?: string
    details?: unknown
  }

  if (res.ok && body.ok) {
    const n = body.count ?? products.length
    s.stop(`Synced ${n} one-time product${n === 1 ? '' : 's'}`)
    return
  }

  s.stop('Product sync skipped')
  p.log.warn(
    `Product sync failed: ${body.error ?? `HTTP ${res.status}`}${
      body.details ? ` — ${JSON.stringify(body.details)}` : ''
    }`,
  )
}

/**
 * Poll the deployed URL until it stops returning Cloudflare's
 * "Assets have not yet deployed" transitional page. Returns true when the
 * worker is actually serving, false on timeout.
 */
async function waitForAssetsReady(url: string, timeoutMs: number): Promise<boolean> {
  const deadline = Date.now() + timeoutMs
  let attempt = 0
  while (Date.now() < deadline) {
    attempt++
    try {
      const res = await fetch(url, { redirect: 'manual' })
      const body = await res.text()
      // The transitional page is HTML with the literal phrase below. Any
      // other body — worker JSON, the app's index.html, even a 404 from the
      // worker's catch-all — means the deploy is live.
      if (!body.includes('Assets have not yet deployed')) {
        return true
      }
    } catch {
      // network blip — try again
    }
    // Exponential backoff capped at 8s: 1s → 2s → 4s → 8s → 8s …
    const wait = Math.min(8_000, 1_000 * 2 ** (attempt - 1))
    await new Promise((r) => setTimeout(r, wait))
  }
  return false
}

function collectAssets(dir: string): Array<{ path: string; contentBase64: string }> {
  const assets: Array<{ path: string; contentBase64: string }> = []
  function walk(d: string, prefix: string) {
    for (const entry of readdirSync(d, { withFileTypes: true })) {
      if (entry.name === '.assetsignore') continue
      const full = join(d, entry.name)
      const rel = prefix ? `${prefix}/${entry.name}` : entry.name
      if (entry.isDirectory()) {
        walk(full, rel)
      } else {
        assets.push({
          path: '/' + rel,
          contentBase64: readFileSync(full).toString('base64'),
        })
      }
    }
  }
  walk(dir, '')
  return assets
}
